import React, { ReactNode } from 'react';
import Media from 'react-media';
import { SerializedStyles } from '@emotion/react';
import { getCustomStyles } from './styles';
import { media } from '../../../styles/MediaQueries';
import { InputHint, Position } from '../../../common/Input/Hint';
import { ViewHint } from '../../../common/Hint';
import { Select as InnerSelect, Props as SelectProps } from '../../Select';
import { FieldContainer } from '../../../common/Input/styles';
import { ValueContainer } from '../ValueContainer';
import { Option } from '../types';

export type HintPosition = Position;

export const viewType = ['small', 'medium', 'big'] as const;
export type ViewType = typeof viewType[number];

export interface CustomProps<T extends Option> {
  name: string;
  label?: string;
  options: T[];
  placeholder?: string;
  view?: ViewType;
  styles?: {
    container?: SerializedStyles;
  };
  hint?: ReactNode;
  hintInfo?: ReactNode;
  hintError?: ReactNode;
  hintWarning?: ReactNode;
  hintView?: ViewHint;
  hintPosition?: HintPosition;
}

export type Props<T extends Option> = CustomProps<T> & Omit<SelectProps, 'options'>;

export function Select<T extends Option>({
  label,
  name,
  view = 'medium',
  hint,
  hintInfo,
  hintError,
  hintWarning,
  hintView = 'error',
  hintPosition = 'right',
  styles,
  placeholder,
  components,
  ...other
}: Props<T>) {
  const isHintVisible = Boolean(hint || hintInfo || hintError || hintWarning);

  return (
    <Media queries={media}>
      {media => {
        const hintPositionAllowable = media.mobile ? 'bottom-static' : hintPosition;

        const isHintDefault =
          hintPosition === 'default' || hintPositionAllowable === 'bottom-static';

        return (
          <FieldContainer
            isHintDefault={isHintDefault}
            isHintVisible={isHintVisible}
            view={view}
            css={styles?.container}
          >
            <InnerSelect
              {...other}
              styles={getCustomStyles({ isHintAtBottom: isHintDefault && isHintVisible })}
              placeholder={label}
              inputPlaceholder={placeholder}
              components={{
                ...components,
                ValueContainer
              }}
            />
            <InputHint
              name={name}
              view={hintView}
              position={hintPositionAllowable}
              content={hint}
              info={hintInfo}
              warning={hintWarning}
              error={hintError}
            />
          </FieldContainer>
        );
      }}
    </Media>
  );
}
